import { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Modal } from '../Modal';
import { useMalfunction } from '../../services/malfunctions';
import { PlaceCenter } from '../PlaceCenter';
import { Spinner } from '../Spinner';
import styles from './MalfunctionPreview.module.css';
import dayjs from 'dayjs';
import { StatusIds, TypeIds } from '../../constants/malfunction';
import AttachmentIcon from '../../assets/icons/attachment.svg?react';
import { useNavigate } from 'react-router-dom';
import { ImageViewer } from '../ImageViewer';

interface MalfunctionPreviewProps {
  open: boolean;
  onClose: () => void;
  id: string | undefined;
}

export const MalfunctionPreview: FC<MalfunctionPreviewProps> = ({
  id,
  ...rest
}) => {
  const { result, running, error } = useMalfunction(id);
  const [img, setImg] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (error) navigate('/malfunctions');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <>
      <Modal title={result?.name} {...rest}>
        {running && (
          <PlaceCenter>
            <Spinner />
          </PlaceCenter>
        )}
        {result && (
          <div className={styles.root} data-testid="malfunction-preview-dialog">
            <section>
              <FormattedMessage tagName="div" id="Malfunction.Vehicle" />
              <div>{result.vehicleName}</div>
              <FormattedMessage tagName="div" id="Malfunction.CreatedAt" />
              <div>{dayjs(result.createdAt).format('YYYY-MM-DD HH:mm')}</div>
              <FormattedMessage tagName="div" id="Malfunction.Reporter" />
              <div>{result.reporter}</div>
              <FormattedMessage tagName="div" id="Malfunction.Type" />
              <FormattedMessage
                tagName="div"
                id={TypeIds.get(result.malfunctionType)}
              />
              <FormattedMessage tagName="div" id="Malfunction.Status" />
              <FormattedMessage
                tagName="div"
                id={StatusIds.get(result.malfunctionStatus)}
              />
            </section>
            {result.description && (
              <section className={styles.description}>
                <FormattedMessage tagName="div" id="Malfunction.Description" />
                <div>{result.description}</div>
              </section>
            )}
            {!!result.photos.length && (
              <div className={styles.files}>
                <div>Files</div>
                <div className={styles.list}>
                  {result.photos.map((photo) => (
                    <div
                      key={photo}
                      className={styles.file}
                      onClick={() => setImg(photo)}
                    >
                      <div className={styles.icon}>
                        <AttachmentIcon />
                      </div>
                      <div className={styles.link}>{photo}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!!result.comments.length && (
              <div className={styles.comments}>
                <div>
                  <FormattedMessage id="Malfunction.Comments" tagName="span" />
                  <FormattedMessage
                    id="Malfunction.Comments.Count"
                    values={{ count: result.comments.length }}
                  >
                    {(node) => <span className={styles.count}>{node}</span>}
                  </FormattedMessage>
                </div>
                {result.comments.map(({ reporter, caption, createdAt, id }) => (
                  <div className={styles.comment} key={id}>
                    <div className={styles.avatar}>
                      {reporter[0].toUpperCase()}
                    </div>
                    <div className={styles.content}>
                      <div className={styles.meta}>
                        <div className={styles.author}>{reporter}</div>
                        <div className={styles.date}>
                          {dayjs(createdAt).format('YYYY-MM-DD HH:mm')}
                        </div>
                      </div>
                      <div className={styles.body}>{caption}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
      <ImageViewer open={!!img} onClose={() => setImg(null)}>
        {img && (
          <img
            src={`/gateway/malfunction-service/v20231213/photos/${img}`}
            alt={img}
          />
        )}
      </ImageViewer>
    </>
  );
};
